import React, { useState } from "react";
import {
  FaShareAlt,
  FaWhatsapp,
  FaFacebookF,
  FaTelegramPlane,
  FaTwitter,
  FaLinkedinIn,
  FaPinterestP,
  FaSms,
  FaEnvelope,
  FaLink,
  FaCheck,
} from "react-icons/fa";
import { elakayAutoText } from "./dataElakay";
import { elakay_API } from "../../config";
import "./ShareButton.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["shareButton"]?.[key] || fallback;

// Bouton de partage : utilise l'API native Web Share (mobile) si disponible —
// sinon affiche un menu avec les plateformes les plus populaires (WhatsApp,
// Facebook, Telegram, Twitter/X, LinkedIn, Pinterest, SMS, Email, Copier le
// lien) pour partager la page avec des amis, la famille et les proches.
//
// The single, shared share implementation for the whole app (ShopUser's
// header, ProductPreview, CardUser's activation modal, addAdminFamily) --
// see mission "une seule logique de partage dans tout le projet". The extra
// props below (disabled/onShared/onCopied/onError/showCopyButton) are all
// optional and additive: existing callers that don't pass them keep the
// exact same behavior.
const ShareButton = ({
  infoSource,
  url,
  title,
  text,
  className = "",
  disabled = false,
  showCopyButton = false,
  // When true, `text` already contains the link (e.g. "message\n\nurl") --
  // never also pass `url` to navigator.share (would duplicate the link on
  // some platforms), and Copy copies `text` instead of the bare `url`.
  combineTextAndUrl = false,
  onShared,
  onCopied,
  onError,
}) => {
  __setElakayInfoSource(infoSource);
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preparedShareUrl, setPreparedShareUrl] = useState("");

  const getShareUrl = () => url || (typeof window !== "undefined" ? window.location.href : "");
  const getShareTitle = () => title || (typeof document !== "undefined" ? document.title : "Elakay");
  const prepareShareUrl = async () => {
    const base = getShareUrl();
    if (combineTextAndUrl) return base;
    try {
      const response = await fetch(`${elakay_API}/social-preview/share-mode`);
      const data = await response.json();
      // Auto keys select the reserved rotating image. Fixed selections also
      // receive a version key so a previously cached social preview cannot
      // hide the image that Family has just saved.
      if (!response.ok || !data.previewKey) return base;
      const next = new URL(base, typeof window !== "undefined" ? window.location.origin : "https://www.elakay.com");
      next.searchParams.set("sharePreview", data.previewKey);
      return next.toString();
    } catch (_) { return base; }
  };

  const handleShareClick = async (e) => {
    e?.stopPropagation();
    if (disabled) return;
    const shareUrl = await prepareShareUrl();
    const shareTitle = getShareTitle();
    const shareText = text || shareTitle;

    if (navigator.share) {
      try {
        const shareData = combineTextAndUrl
          ? { title: shareTitle, text: shareText }
          : { title: shareTitle, text: shareText, url: shareUrl };
        await navigator.share(shareData);
        onShared?.();
      } catch (err) {
        // AbortError = l'utilisateur a annulé le partage — jamais une erreur.
        if (err?.name !== "AbortError") onError?.(err);
      }
      return;
    }

    setPreparedShareUrl(shareUrl);
    setShowMenu((prev) => !prev);
  };

  const handleCopyLink = async (e) => {
    e?.stopPropagation();
    if (disabled) return;
    const nextShareUrl = await prepareShareUrl();
    const valueToCopy = combineTextAndUrl ? (text || nextShareUrl) : nextShareUrl;
    try {
      // await navigator.clipboard.writeText(valueToCopy);
      await navigator.clipboard.writeText(valueToCopy);
      setPreparedShareUrl(nextShareUrl);
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard indisponible — on laisse le lien visible dans le menu.
      onError?.(err);
    }
  };

  const shareUrl = preparedShareUrl || getShareUrl();
  const shareTitle = getShareTitle();
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  return (
    <div className={`elakay-share ${className}`}>
      <button
        type="button"
        className="elakay-share-btn"
        onClick={handleShareClick}
        aria-label={__elakayText("t001", "Share")}
        title={__elakayText("t001", "Share")}
        disabled={disabled}
      >
        <FaShareAlt />
      </button>

      {showCopyButton && (
        <button
          type="button"
          className="elakay-share-copy-btn"
          onClick={handleCopyLink}
          aria-label={copied ? __elakayText("t003", "Link copied!") : __elakayText("t004", "Copy link")}
          title={copied ? __elakayText("t003", "Link copied!") : __elakayText("t004", "Copy link")}
          disabled={disabled}
        >
          {copied ? <FaCheck /> : <FaLink />}
        </button>
      )}

      {showMenu && (
        <>
          <div className="elakay-share-backdrop" onClick={() => setShowMenu(false)} />
          <div className="elakay-share-menu">
            <a
              className="elakay-share-menu-item"
              href={`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaWhatsapp className="elakay-share-icon elakay-share-icon--whatsapp" />
              {elakayAutoText("h_3f7c0f53e1d4a9", "WhatsApp")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaFacebookF className="elakay-share-icon elakay-share-icon--facebook" />
              {elakayAutoText("h_1d9e7e0f4d6e21", "Facebook")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaTelegramPlane className="elakay-share-icon elakay-share-icon--telegram" />
              {elakayAutoText("h_8b6f3a2c4d5e10", "Telegram")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaTwitter className="elakay-share-icon elakay-share-icon--twitter" />
              {elakayAutoText("h_2a9c7e1b3f4d22", "Twitter / X")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaLinkedinIn className="elakay-share-icon elakay-share-icon--linkedin" />
              {elakayAutoText("h_5d4c3b2a1f6e33", "LinkedIn")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <FaPinterestP className="elakay-share-icon elakay-share-icon--pinterest" />
              {elakayAutoText("h_7e6d5c4b3a2f44", "Pinterest")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`sms:?body=${encodedTitle}%20${encodedUrl}`}
              onClick={() => setShowMenu(false)}
            >
              <FaSms className="elakay-share-icon elakay-share-icon--sms" />
              {__elakayText("t005", "Text message")}
            </a>
            <a
              className="elakay-share-menu-item"
              href={`mailto:?subject=${encodedTitle}&body=${encodedUrl}`}
              onClick={() => setShowMenu(false)}
            >
              <FaEnvelope className="elakay-share-icon elakay-share-icon--email" />
              {__elakayText("t002", "Email")}
            </a>
            <button type="button" className="elakay-share-menu-item" onClick={handleCopyLink}>
              <FaLink className="elakay-share-icon elakay-share-icon--link" />
              {copied ? __elakayText("t003", "Link copied!") : __elakayText("t004", "Copy link")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareButton;
